import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EVENTS, Joyride, STATUS } from 'react-joyride';
import type { EventHandler, Step } from 'react-joyride';
import { TourTooltip } from './TourTooltip';
import { filterValidSteps, useJoyrideOptions } from './tourUtils';

interface ExperimentsAnnouncementTourProps {
  open: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

/** Delay (ms) after `open` flips true before resolving sidebar anchors. */
const ANCHOR_MOUNT_DELAY = 300;

export const ExperimentsAnnouncementTour = ({ open, onComplete, onSkip }: ExperimentsAnnouncementTourProps) => {
  const { t } = useTranslation('common');
  const joyrideOptions = useJoyrideOptions();
  const [steps, setSteps] = useState<Step[]>([]);

  useEffect(() => {
    if (!open) {
      setSteps([]);
      return;
    }

    const timer = window.setTimeout(() => {
      const candidates: Step[] = [
        {
          target: 'body',
          placement: 'center',
          title: `🧪 ${t('experimentsAnnouncement.title', 'New: Experiments!')}`,
          content: t(
            'experimentsAnnouncement.intro',
            "Scribelate now has an Experiments section in the sidebar — a home for early-access features we're still polishing."
          ),
        },
        {
          target: '[data-tour="nav-experiments"]',
          placement: 'right',
          // Allow clicking the spotlit Experiments group toggle
          blockTargetInteraction: false,
          title: t('experimentsAnnouncement.navTitle', 'Find it in the sidebar'),
          content: t(
            'experimentsAnnouncement.navDescription',
            "Click Experiments to expand it. Inside you'll find StoryTime — AI-generated stories built from the vocabulary you've collected."
          ),
        },
      ];

      // On mobile the temporary drawer is closed, so only the centered
      // intro step survives — acceptable.
      setSteps(filterValidSteps(candidates));
    }, ANCHOR_MOUNT_DELAY);

    return () => window.clearTimeout(timer);
  }, [open, t]);

  // Clicking the spotlit Experiments toggle expands the group, which is the
  // outcome this announcement drives — treat it as completion. Capture phase
  // ensures this runs before the toggle's own click handler.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleNavClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-tour="nav-experiments"]')) {
        onComplete();
      }
    };
    document.addEventListener('click', handleNavClick, true);
    return () => document.removeEventListener('click', handleNavClick, true);
  }, [open, onComplete]);

  const handleEvent: EventHandler = (data) => {
    if (data.type !== EVENTS.TOUR_END) {
      return;
    }
    if (data.status === STATUS.FINISHED) {
      // Persists experiments_announcement_completed
      onComplete();
    } else if (data.status === STATUS.SKIPPED) {
      // Persists experiments_announcement_skipped (re-offers next visit)
      onSkip();
    }
  };

  if (!open || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      run
      continuous
      steps={steps}
      tooltipComponent={TourTooltip}
      onEvent={handleEvent}
      options={{
        ...joyrideOptions,
        closeButtonAction: 'skip',
        overlayClickAction: false,
      }}
    />
  );
};
